import React, { useState, useEffect } from 'react';
import { FileUploader } from './components/FileUploader';
import { ReportIssue } from './components/ReportIssue';
import { FeedbackButton } from './components/FeedbackButton';
import { authService } from './services/auth';
import { config } from './config/index';
import { LogOut, User, FileText, Clock, CheckCircle } from 'lucide-react';
import './App.css';

interface Result {
  job_id: string;
  status: string;
  result?: {
    titre: string;
    resume: string;
  };
  created_at: string;
  file_name?: string;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [currentResult, setCurrentResult] = useState<Result | null>(null);
  const [userResults, setUserResults] = useState<Result[]>([]);
  const [expandedResult, setExpandedResult] = useState<string | null>(null);
  const [loadingUserResults, setLoadingUserResults] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);

  // Check if user has access to this tool based on Cognito groups
  const checkUserAccess = (userInfo: any): boolean => {
    if (!userInfo) return false;

    const userGroups = userInfo['cognito:groups'] || [];
    const hostname = window.location.hostname;

    // Determine which tool we're on
    const isTitreTool = hostname.includes('titre') || hostname.includes('demo') || hostname.includes('d333rwauucv1ib');
    const isWebTool = hostname.includes('web.thorpodcast');

    // all-access group has access to everything
    if (userGroups.includes('all-access')) {
      return true;
    }

    // Check tool-specific access
    if (isTitreTool && userGroups.includes('titre-only')) {
      return true;
    }

    if (isWebTool && userGroups.includes('web-only')) {
      return true;
    }

    // If user has no groups, grant access by default (backwards compatibility)
    if (userGroups.length === 0) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    // Check if returning from Cognito login
    if (window.location.hash) {
      const success = authService.handleCallback();
      if (success) {
        const userInfo = authService.getUser();
        setIsAuthenticated(true);
        setUser(userInfo);
        setHasAccess(checkUserAccess(userInfo));
      }
    } else if (authService.isAuthenticated()) {
      const userInfo = authService.getUser();
      setIsAuthenticated(true);
      setUser(userInfo);
      setHasAccess(checkUserAccess(userInfo));
    }
  }, []);

  // Load user results when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadUserResults();
    }
  }, [isAuthenticated]);

  const handleLogin = () => {
    authService.login();
  };

  const handleLogout = () => {
    authService.logout();
  };

  const loadUserResults = async () => {
    setLoadingUserResults(true);
    try {
      // For now, we'll store results locally
      // In production, you'd want to filter by user ID from an API
      const storedResults = localStorage.getItem(`user_results_${user?.sub}`);
      if (storedResults) {
        const results = JSON.parse(storedResults);
        // Keep only the 5 most recent
        setUserResults(results.slice(0, 5));
      }
    } catch (error) {
      console.error('Error loading user results:', error);
    } finally {
      setLoadingUserResults(false);
    }
  };

  const saveUserResult = (result: Result) => {
    try {
      const storedResults = localStorage.getItem(`user_results_${user?.sub}`);
      let results = storedResults ? JSON.parse(storedResults) : [];
      results = [result, ...results].slice(0, 10); // Keep max 10 in storage
      localStorage.setItem(`user_results_${user?.sub}`, JSON.stringify(results));
    } catch (error) {
      console.error('Error saving user result:', error);
    }
  };

  const handleUploadComplete = (result: Result) => {
    // Set as current result
    setCurrentResult(result);
    setExpandedResult(result.job_id);
    
    // Save to local storage and update user results
    saveUserResult(result);
    
    // Update the list of user results (keeping only 5)
    setUserResults(prev => [result, ...prev.filter(r => r.job_id !== result.job_id)].slice(0, 5));
  };

  const toggleResult = (jobId: string) => {
    setExpandedResult(expandedResult === jobId ? null : jobId);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="app">
        <div className="login-container">
          <div className="login-card">
            <div className="logo">
              <img src="/thor-logo.png" alt="Thor Podcast" style={{width: '200px', height: 'auto'}} />
            </div>
            <h1>Thor Podcast pour KTO</h1>
            <p className="version">Version {config.app.version}</p>
            <p className="description">
              Générez automatiquement des titres et résumés attractifs pour vos contenus
            </p>
            <div className="features">
              <div className="feature">
                <CheckCircle size={20} />
                <span>Support multi-format (PDF, DOCX, TXT...)</span>
              </div>
              <div className="feature">
                <CheckCircle size={20} />
                <span>Traitement rapide par IA</span>
              </div>
            </div>
            <button onClick={handleLogin} className="login-button">
              Se connecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show access denied if user doesn't have permission
  if (!hasAccess) {
    return (
      <div className="app">
        <div className="login-container">
          <div className="login-card">
            <div className="logo">
              <img src="/thor-logo.png" alt="Thor Podcast" style={{width: '200px', height: 'auto'}} />
            </div>
            <h1 style={{color: '#dc2626'}}>Accès Non Autorisé</h1>
            <p className="description" style={{color: '#64748b', marginBottom: '24px'}}>
              Votre compte n'a pas les permissions nécessaires pour accéder à cet outil.
            </p>
            <p style={{color: '#94a3b8', fontSize: '14px', marginBottom: '24px'}}>
              Connecté en tant que : <strong>{user?.email || user?.username}</strong>
            </p>
            <button onClick={handleLogout} className="login-button" style={{background: '#dc2626'}}>
              Se déconnecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <div className="header-left">
            <a href="https://thorpodcast.link/login.html" style={{textDecoration: 'none', display: 'flex', alignItems: 'center'}}>
              <img src="/thor-logo.png" alt="Thor Podcast" style={{height: '40px', width: 'auto'}} />
            </a>
            <nav className="header-nav">
              <a href="https://thorpodcast.link/login.html" className="nav-link">
                <User size={16} />
                Accueil
              </a>
              <a href="https://titre.thorpodcast.link" className="nav-link active">
                <FileText size={16} />
                Titre & Résumé
              </a>
              <a href="https://web.thorpodcast.link" className="nav-link">
                <FileText size={16} />
                Article Web
              </a>
            </nav>
          </div>
          <div className="header-right">
            <div className="user-info">
              <User size={20} />
              <span>{user?.username}</span>
              <span className="user-group">{user?.group}</span>
            </div>
            <button onClick={handleLogout} className="logout-button">
              <LogOut size={20} />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="app-main">
        <div className="container">
          <section className="upload-section">
            <h2>Nouveau fichier</h2>
            <FileUploader onUploadComplete={handleUploadComplete} />
          </section>

          {/* Current Result Section */}
          {currentResult && (
            <section className="current-result-section">
              <h2>
                <CheckCircle size={20} />
                Résultat actuel
              </h2>
              <div className="result-card featured">
                <div 
                  className="result-header"
                  onClick={() => toggleResult(currentResult.job_id)}
                >
                  <div className="result-title">
                    {currentResult.result?.titre || 'En cours de traitement...'}
                  </div>
                  <div className="result-meta">
                    <Clock size={14} />
                    <span>{formatDate(currentResult.created_at)}</span>
                    {currentResult.file_name && (
                      <>
                        <FileText size={14} />
                        <span>{currentResult.file_name}</span>
                      </>
                    )}
                  </div>
                </div>
                
                {expandedResult === currentResult.job_id && currentResult.result && (
                  <div className="result-content">
                    <div className="result-section">
                      <h3>Titre généré:</h3>
                      <p className="result-titre">{currentResult.result.titre}</p>
                    </div>
                    <div className="result-section">
                      <h3>Résumé:</h3>
                      <p className="result-resume">{currentResult.result.resume}</p>
                    </div>
                    <button 
                      className="copy-button"
                      onClick={() => {
                        const text = `TITRE: ${currentResult.result!.titre}\n\nRÉSUMÉ: ${currentResult.result!.resume}`;
                        navigator.clipboard.writeText(text);
                      }}
                    >
                      Copier le résultat
                    </button>
                    <FeedbackButton 
                      jobId={currentResult.job_id}
                      category={user?.group}
                      onRegenerated={(newResult) => {
                        const updatedResult = {
                          ...currentResult,
                          result: {
                            titre: newResult.title,
                            resume: newResult.summary
                          }
                        };
                        setCurrentResult(updatedResult);
                        // Also update in localStorage
                        saveUserResult(updatedResult);
                        // Update in userResults if it exists there
                        setUserResults(prev => prev.map(r => 
                          r.job_id === currentResult.job_id ? updatedResult : r
                        ));
                      }}
                    />
                  </div>
                )}
              </div>
            </section>
          )}

          {/* User's Recent Results Section */}
          {userResults.length > 0 && (
            <section className="user-results-section">
              <h2>
                <Clock size={20} />
                Mes 5 derniers résultats
              </h2>
              <div className="results-list">
                {userResults.map((result) => (
                  <div key={result.job_id} className="result-card">
                    <div 
                      className="result-header"
                      onClick={() => toggleResult(result.job_id)}
                    >
                      <div className="result-title">
                        {result.result?.titre || result.file_name || 'Sans titre'}
                      </div>
                      <div className="result-meta">
                        <Clock size={14} />
                        <span>{formatDate(result.created_at)}</span>
                        {result.file_name && (
                          <>
                            <FileText size={14} />
                            <span>{result.file_name}</span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {expandedResult === result.job_id && result.result && (
                      <div className="result-content">
                        <p className="result-resume">{result.result.resume}</p>
                        <button 
                          className="copy-button small"
                          onClick={(e) => {
                            e.stopPropagation();
                            const text = `TITRE: ${result.result!.titre}\n\nRÉSUMÉ: ${result.result!.resume}`;
                            navigator.clipboard.writeText(text);
                          }}
                        >
                          Copier
                        </button>
                        <FeedbackButton 
                          jobId={result.job_id}
                          category={user?.group}
                          onRegenerated={(newResult) => {
                            const updatedResults = userResults.map(r => 
                              r.job_id === result.job_id 
                                ? { ...r, result: { titre: newResult.title, resume: newResult.summary } }
                                : r
                            );
                            setUserResults(updatedResults);
                            saveUserResult(updatedResults.find(r => r.job_id === result.job_id)!);
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

        </div>
      </main>

      <footer className="app-footer">
        <p>Thor Podcast v{config.app.version} © 2025 Premiere Pierre Media</p>
      </footer>
      
      <ReportIssue 
        userGroup={user?.group}
        lastFileName={currentResult?.file_name}
      />
    </div>
  );
}

export default App;
